import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, CheckCircle2, XCircle, ClipboardCheck } from 'lucide-react';
import { Button, Card } from '../components';
import { getAuditTrainings, zetAuditUitslag } from '../services/opleidingenApi';
import type { AuditStatus, AuditTraining } from '../services/opleidingenApi';

/**
 * Admin: compliance-keuring van ingediende cursussen (Target Scope Wizard).
 * Server: /api/opleidingen/admin/audit + /admin/trainings/:id/audit (migratie 150).
 * De uitslag landt als SIGNAL_COURSE_AUDIT_RESULT in de meldingenfeed van de opleider.
 */

const RIJEN = ['MENTAL', 'PHYSICAL', 'FINANCIAL', 'GROWTH'] as const;
const STATUSSEN: AuditStatus[] = ['IN_REVIEW', 'APPROVED', 'REJECTED'];

const AdminAcademyAuditPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuditStatus>('IN_REVIEW');
  const [afkeuren, setAfkeuren] = useState<AuditTraining | null>(null);
  const [toelichting, setToelichting] = useState('');
  const [rij, setRij] = useState<string>('');

  const lijst = useQuery({ queryKey: ['admin-academy-audit', status], queryFn: () => getAuditTrainings(status) });

  const uitslag = useMutation({
    mutationFn: (v: { id: number; status: 'APPROVED' | 'REJECTED'; toelichting?: string | null; aanbevolen_rij?: string | null }) =>
      zetAuditUitslag(v.id, { status: v.status, toelichting: v.toelichting ?? null, aanbevolen_rij: v.aanbevolen_rij ?? null }),
    onSuccess: () => {
      toast.success(t('adminAcademyAudit.saved'));
      setAfkeuren(null);
      setToelichting('');
      setRij('');
      queryClient.invalidateQueries({ queryKey: ['admin-academy-audit'] });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('adminAcademyAudit.error')),
  });

  const rijLabel = (r: AuditTraining) => r.academy_categorie || r.b2b_rij || r.pro_hoofdmap || '—';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <button type="button" onClick={() => navigate('/admin')} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t('adminAcademyAudit.back')}
        </button>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-teal-600" /> {t('adminAcademyAudit.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('adminAcademyAudit.subtitle')}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {STATUSSEN.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                status === s ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              {t(`adminAcademyAudit.status_${s}`)}
            </button>
          ))}
        </div>

        <Card>
          {lijst.isLoading ? (
            <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto" /></div>
          ) : (lijst.data ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">{t('adminAcademyAudit.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{t('adminAcademyAudit.colTraining')}</th>
                    <th className="px-4 py-2">{t('adminAcademyAudit.colProvider')}</th>
                    <th className="px-4 py-2">{t('adminAcademyAudit.colRow')}</th>
                    <th className="px-4 py-2">{t('adminAcademyAudit.colDate')}</th>
                    {status !== 'IN_REVIEW' && <th className="px-4 py-2">{t('adminAcademyAudit.colNote')}</th>}
                    <th className="px-4 py-2 text-right">{t('adminAcademyAudit.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(lijst.data ?? []).map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.titel}</div>
                        <div className="text-xs text-gray-500">{r.soort_opleiding || '—'} · {r.is_published ? t('adminAcademyAudit.published') : t('adminAcademyAudit.draft')}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.kvk_handelsnaam || r.organisatie_naam || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{rijLabel(r)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(status === 'IN_REVIEW' ? r.created_at : r.audit_beslist_at || r.created_at).toLocaleDateString('nl-NL')}
                      </td>
                      {status !== 'IN_REVIEW' && (
                        <td className="px-4 py-3 text-gray-600 max-w-xs">
                          {r.audit_toelichting || '—'}
                          {r.audit_aanbevolen_rij && <span className="block text-xs text-gray-400">→ {r.audit_aanbevolen_rij}</span>}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {r.audit_status !== 'APPROVED' && (
                            <Button
                              size="sm"
                              onClick={() => uitslag.mutate({ id: r.id, status: 'APPROVED' })}
                              disabled={uitslag.isPending}
                              className="flex items-center gap-1"
                            >
                              <CheckCircle2 className="w-4 h-4" /> {t('adminAcademyAudit.approve')}
                            </Button>
                          )}
                          {r.audit_status !== 'REJECTED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setAfkeuren(r); setToelichting(''); setRij(''); }}
                              disabled={uitslag.isPending}
                              className="flex items-center gap-1 text-red-700 border-red-200 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4" /> {t('adminAcademyAudit.reject')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {afkeuren && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAfkeuren(null)}>
            <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-gray-900">{t('adminAcademyAudit.rejectTitle', { title: afkeuren.titel })}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('adminAcademyAudit.rejectHint')}</p>
              <label className="block mt-4 text-sm font-medium text-gray-700">
                {t('adminAcademyAudit.noteLabel')}
                <textarea
                  value={toelichting}
                  onChange={(e) => setToelichting(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                  placeholder={t('adminAcademyAudit.notePlaceholder')}
                />
              </label>
              <label className="block mt-3 text-sm font-medium text-gray-700">
                {t('adminAcademyAudit.rowLabel')}
                <select value={rij} onChange={(e) => setRij(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">{t('adminAcademyAudit.rowNone')}</option>
                  {RIJEN.map((r) => (
                    <option key={r} value={r}>{t(`adminAcademyAudit.row_${r}`)}</option>
                  ))}
                </select>
              </label>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAfkeuren(null)}>{t('adminAcademyAudit.cancel')}</Button>
                <Button
                  onClick={() => uitslag.mutate({ id: afkeuren.id, status: 'REJECTED', toelichting: toelichting.trim(), aanbevolen_rij: rij || null })}
                  disabled={uitslag.isPending || toelichting.trim().length < 10}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t('adminAcademyAudit.confirmReject')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAcademyAuditPage;
